/**
 * Exceptions and their resolution proposals (INV-02 "Exceptions", POST
 * `/v1/invoice-resolutions`).
 *
 * The four-eyes rule is the point of this panel, and the boundary is drawn here:
 *
 *   - A resolution is **proposed** here. The proposal records the proposer and
 *     moves no money.
 *   - **Approval is never offered on this page.** The approve/reject step lives
 *     on the resolution approvals page under the approver's own permission and a
 *     fresh step-up, and this panel says so and links there read-only. There is
 *     no approve control, no disabled approve control and no client-side
 *     "you may approve" check anywhere in this feature — the service decides, and
 *     the server's refusal is what a person sees.
 *
 * Amount entry goes through `parseDecimalToBaseUnits`, so what is typed is turned
 * into base units with BigInt and a value carrying more decimals than the asset
 * has is refused (`AMOUNT_PRECISION`, the same code the service would send)
 * *before* anything is sent — and the page says it was checked here, not there.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ApiErrorInfo } from "@/lib/api";
import { idempotencyKey } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { Modal } from "@/components/ui/modal";
import { RadioGroup, Textarea } from "@/components/ui/form-controls";
import { Field, Input } from "@/components/ui/input";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { LoadingTable, NotAvailableState } from "@/components/ui/system-state";
import { MoneyError, formatMoney, parseDecimalToBaseUnits } from "@/features/invoicing/money";
import { proposeInvoiceResolution } from "@/features/invoicing/api";
import {
  exceptionStatePresentation,
  exceptionTypePresentation,
  resolutionKindPresentation,
} from "@/features/invoicing/status";
import type {
  Exception,
  ProposeResolutionRequest,
  Resolution,
  ResolutionKind,
} from "@/features/invoicing/types";
import { DETAIL_COPY } from "./copy";
import { RefusalNotice } from "./refusal";
import { useInvoicingScope } from "@/features/invoicing/invoices-list/use-invoicing-scope";
import { StatusChip } from "./status-axes";

const KIND_OPTIONS: { value: ResolutionKind; label: string; description: string }[] = [
  { value: "APPLY", label: DETAIL_COPY.proposeKindApply, description: DETAIL_COPY.proposeKindApplyDescription },
  { value: "KEEP_AS_CREDIT", label: DETAIL_COPY.proposeKindKeep, description: DETAIL_COPY.proposeKindKeepDescription },
  { value: "WRITE_OFF", label: DETAIL_COPY.proposeKindWriteOff, description: DETAIL_COPY.proposeKindWriteOffDescription },
  { value: "REFUND", label: DETAIL_COPY.proposeKindRefund, description: DETAIL_COPY.proposeKindRefundDescription },
];

function ProposeResolutionModal({
  exception,
  initialKind,
  open,
  onOpenChange,
  onProposed,
}: {
  exception: Exception;
  initialKind: ResolutionKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProposed: (resolution: Resolution) => void;
}) {
  const canResolve = useInvoicingScope("invoice:resolve.propose");
  const canRefund = useInvoicingScope("invoice:refund.propose");
  const [kind, setKind] = useState<ResolutionKind>(initialKind);
  const [amountText, setAmountText] = useState(() => formatMoney(exception.amount));
  const [targetInvoiceId, setTargetInvoiceId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // `source` is tracked, never inferred from the presence of a support
  // reference: the header that carries one is not on every response yet, so
  // inferring would label a service refusal as a console check.
  const [refusal, setRefusal] = useState<{ error: ApiErrorInfo; source: "service" | "console" } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // One key per proposal intent, reused if the operator retries the same
  // proposal after a transport failure, so the service replays its first result
  // instead of recording a second proposal.
  const keyRef = useRef<string | null>(null);

  const allowed = kind === "REFUND" ? canRefund : canResolve;
  const choices = KIND_OPTIONS.filter((option) => option.value === "REFUND" ? canRefund : canResolve);
  const decimals = exception.amount.asset.decimals;
  const symbol = exception.amount.asset.symbol;

  function reset() {
    setKind(initialKind);
    setAmountText(formatMoney(exception.amount));
    setTargetInvoiceId("");
    setReason("");
    setRefusal(null);
    setFormError(null);
    keyRef.current = null;
  }

  async function submit() {
    if (!allowed || busy) return;
    setFormError(null);
    setRefusal(null);
    if (reason.trim() === "") {
      setFormError(DETAIL_COPY.proposeReasonRequired);
      return;
    }
    if (kind === "APPLY" && targetInvoiceId.trim() === "") {
      setFormError(DETAIL_COPY.proposeTargetRequired);
      return;
    }
    // Base units, computed with BigInt and refused here if the asset cannot
    // carry the value. Never `Number(...)`, never a float.
    let amount: string;
    try {
      amount = parseDecimalToBaseUnits(amountText, decimals);
    } catch (error) {
      setRefusal({
        error: { reasonCode: error instanceof MoneyError ? error.code : "INVALID_AMOUNT" },
        source: "console",
      });
      return;
    }

    const body: ProposeResolutionRequest = {
      sourceType: "EXCEPTION",
      exceptionId: exception.exceptionId,
      kind,
      amount: { amount, asset: { symbol, decimals } },
      reason: reason.trim(),
      ...(kind === "APPLY" ? { targetInvoiceId: targetInvoiceId.trim() } : {}),
    };

    setBusy(true);
    if (!keyRef.current) keyRef.current = idempotencyKey("invoicing_resolution_propose");
    const result = await proposeInvoiceResolution(body, { idempotencyKey: keyRef.current });
    setBusy(false);
    if (!result.ok) {
      setRefusal({ error: result.error, source: "service" });
      return;
    }
    keyRef.current = null;
    onProposed(result.data);
    reset();
    onOpenChange(false);
  }

  return (
    <Modal
      open={open && allowed}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title={initialKind === "REFUND" ? DETAIL_COPY.proposeTitleRefund : DETAIL_COPY.proposeTitleResolve}
      description={DETAIL_COPY.proposalsOpenNote}
      size="lg"
      footer={
        <>
          <Button
            variant="ghost"
            size="md"
            disabled={busy}
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            {DETAIL_COPY.proposeCancel}
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={busy}
            disabled={!allowed}
            onClick={() => void submit()}
            data-testid="propose-resolution-submit"
          >
            {DETAIL_COPY.proposeSubmit}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <RadioGroup
          ariaLabel={DETAIL_COPY.proposeKindLabel}
          value={kind}
          onChange={(value) => setKind(value as ResolutionKind)}
          options={choices}
        />

        <Field
          label={DETAIL_COPY.proposeAmountLabel}
          htmlFor="propose-amount"
          hint={DETAIL_COPY.proposeAmountHint(symbol, decimals)}
        >
          <Input
            id="propose-amount"
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            inputMode="decimal"
            autoComplete="off"
          />
        </Field>

        {kind === "APPLY" && (
          <Field
            label={DETAIL_COPY.proposeTargetInvoiceLabel}
            htmlFor="propose-target"
            hint={DETAIL_COPY.proposeTargetInvoiceHint}
          >
            <Input
              id="propose-target"
              value={targetInvoiceId}
              onChange={(e) => setTargetInvoiceId(e.target.value)}
              autoComplete="off"
            />
          </Field>
        )}

        <Field
          label={DETAIL_COPY.proposeReasonLabel}
          htmlFor="propose-reason"
          hint={DETAIL_COPY.proposeReasonHint}
        >
          <Textarea
            id="propose-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>

        {formError && (
          <p role="alert" className="text-sm text-danger">
            {formError}
          </p>
        )}
        {refusal && (
          <RefusalNotice error={refusal.error} title={DETAIL_COPY.refusedTitle} source={refusal.source} />
        )}
      </div>
    </Modal>
  );
}

export function ExceptionsPanel({
  exceptions,
  loading,
  error,
  refresh,
}: {
  exceptions: Exception[];
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}) {
  const canResolve = useInvoicingScope("invoice:resolve.propose");
  const canRefund = useInvoicingScope("invoice:refund.propose");
  const [proposing, setProposing] = useState<{ exception: Exception; kind: ResolutionKind } | null>(null);
  const [receipt, setReceipt] = useState<{ resolutionId: string; kind: ResolutionKind } | null>(null);

  useEffect(() => {
    if (proposing && !(proposing.kind === "REFUND" ? canRefund : canResolve)) setProposing(null);
  }, [proposing, canResolve, canRefund]);

  return (
    <section aria-labelledby="invoice-exceptions-heading" data-testid="invoice-exceptions">
      <h2 id="invoice-exceptions-heading" className="mb-2 text-sm font-bold text-ink">
        {DETAIL_COPY.exceptionsHeading}
      </h2>

      {receipt && (
        <p role="status" className="mb-3 rounded-md border border-success-border bg-success-soft px-3.5 py-2.5 text-sm text-success">
          {DETAIL_COPY.proposeSubmitted(receipt.resolutionId, resolutionKindPresentation(receipt.kind).label)}
        </p>
      )}

      {loading && <LoadingTable rows={2} cols={4} />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-2">
          <NotAvailableState
            title={DETAIL_COPY.exceptionsUnavailableTitle}
            description={DETAIL_COPY.exceptionsUnavailableDescription}
            reason={error.reasonCode}
          />
          <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          <Button variant="secondary" size="sm" onClick={refresh}>
            {DETAIL_COPY.retry}
          </Button>
        </div>
      )}

      {!loading && !error && exceptions.length === 0 && (
        <EmptyState card title={DETAIL_COPY.exceptionsEmptyTitle} description={DETAIL_COPY.exceptionsEmptyDescription} />
      )}

      {!loading && !error && exceptions.length > 0 && (
        <ul className="flex flex-col gap-3">
          {exceptions.map((exception) => {
            const canPropose = exception.state === "OPEN" && (canResolve || canRefund);
            return (
              <li key={exception.exceptionId} className="rounded-lg border border-line bg-surface px-4 py-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <StatusChip
                    presentation={exceptionTypePresentation(exception.type)}
                    raw={String(exception.type)}
                    testId={`invoice-exception-type-${exception.exceptionId}`}
                  />
                  <StatusChip
                    presentation={exceptionStatePresentation(exception.state)}
                    raw={String(exception.state)}
                    testId={`invoice-exception-state-${exception.exceptionId}`}
                  />
                  <span className="font-semibold text-ink">{formatMoney(exception.amount)}</span>
                  <span className="text-sm text-muted">{exception.amount.asset.symbol}</span>
                  <code dir="ltr" className="break-all font-mono text-[11px] text-muted">
                    {exception.exceptionId}
                  </code>
                </div>

                <div className="mt-1 flex flex-wrap items-baseline gap-x-3 text-xs text-muted">
                  <span>
                    {DETAIL_COPY.exceptionCreatedAt} {exception.createdAt}
                  </span>
                  {exception.reconExceptionRef && (
                    <span>
                      {DETAIL_COPY.exceptionReconRef} <code dir="ltr">{exception.reconExceptionRef}</code>
                    </span>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {canPropose ? (
                    <>
                      {canResolve && <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`invoice-propose-resolution-${exception.exceptionId}`}
                        onClick={() => setProposing({ exception, kind: "KEEP_AS_CREDIT" })}
                      >
                        {DETAIL_COPY.proposeResolutionCta}
                      </Button>}
                      {canRefund && <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`invoice-propose-refund-${exception.exceptionId}`}
                        onClick={() => setProposing({ exception, kind: "REFUND" })}
                      >
                        {DETAIL_COPY.proposeRefundCta}
                      </Button>}
                    </>
                  ) : (
                    <span className="text-xs text-muted">
                      {exception.state === "RESOLUTION_PROPOSED"
                        ? DETAIL_COPY.exceptionApprovalPending
                        : exceptionStatePresentation(exception.state).label}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="max-w-[680px] text-xs leading-relaxed text-muted">
          {DETAIL_COPY.approveLivesElsewhere}
        </p>
        <Link
          to="/stablecoins/invoices/resolutions"
          className="text-xs font-semibold text-primary underline"
          data-testid="resolution-approvals-link"
        >
          {DETAIL_COPY.approvalLinkLabel}
        </Link>
      </div>

      {proposing && (proposing.kind === "REFUND" ? canRefund : canResolve) && (
        <ProposeResolutionModal
          exception={proposing.exception}
          initialKind={proposing.kind}
          open
          onOpenChange={(next) => {
            if (!next) setProposing(null);
          }}
          onProposed={(resolution) => {
            setReceipt({ resolutionId: resolution.resolutionId, kind: resolution.kind });
            refresh();
          }}
        />
      )}
    </section>
  );
}
